import { ChevronRight } from "@cynthai/icons";
import { Link as RouterLink } from "@tanstack/react-router";
import type { ReactNode } from "react";
import {
  Breadcrumbs as RABreadcrumbs,
  Breadcrumb as RABreadcrumb,
} from "react-aria-components";
import type { VariantProps } from "tailwind-variants";
import { tv } from "tailwind-variants";

const breadcrumbs = tv({
  slots: {
    container: "flex items-center",
    item: `
      flex items-center text-text-secondary
      last:text-text-primary
    `,
    link: "flex items-center",
    text: "flex items-center",
    separator: "text-text-secondary",
  },
  variants: {
    spacing: {
      tight: {
        container: "gap-2",
        item: "gap-2",
      },
      normal: {
        container: "gap-4",
        item: "gap-4",
      },
      loose: {
        container: "gap-6",
        item: "gap-6",
      },
    },
    size: {
      sm: {
        item: "text-xs",
      },
      md: {
        item: "text-sm",
      },
      lg: {
        item: "text-base",
      },
    },
  },
  defaultVariants: {
    spacing: "tight",
    size: "md",
  },
});

export interface BreadcrumbItem {
  href?: string;
  label: string | ReactNode;
}

export interface BreadcrumbsProps extends VariantProps<typeof breadcrumbs> {
  readonly items: BreadcrumbItem[];
  readonly className?: string;
  readonly icon?: React.ComponentType<{
    className?: string;
    height?: number;
    width?: number;
  }>;
  readonly iconSize?: number;
}

export default function Breadcrumbs({
  items,
  className,
  spacing,
  size,
  icon: IconComponent = ChevronRight,
  iconSize = 14,
}: Readonly<BreadcrumbsProps>) {
  const styles = breadcrumbs({ spacing, size });

  return (
    <RABreadcrumbs className={styles.container({ className })}>
      {items.map((item, index) => {
        const isLast = index === items.length - 1;
        const hasLink = !!item.href;

        return (
          // oxlint-disable-next-line react/no-array-index-key
          <RABreadcrumb key={index} className={styles.item()}>
            {hasLink ? (
              <RouterLink to={item.href} className={styles.link()}>
                {item.label}
              </RouterLink>
            ) : (
              <span className={styles.text()}>{item.label}</span>
            )}
            {!isLast && (
              <IconComponent
                className={styles.separator()}
                height={iconSize}
                width={iconSize}
              />
            )}
          </RABreadcrumb>
        );
      })}
    </RABreadcrumbs>
  );
}
